import dialogPolyfill from 'dialog-polyfill';

const _elements = {
    dialog: null, // The dialog instance which will be reused.
    header: null,
    content: null,
    actions: null
};
const _currentActionKeys = []; // Keyboard keys that we'll react to, set on init.

function init(header, content, actions, actionKeys) {
    // If the dialog hasn't been created yet then do so now.
    if (!_elements.dialog) {
        init_createDialog();
    }

    // Dialog was (or has now) been created, so now we can set the content appropriately...

    init_setHeader(header);
    init_setContent(content);
    init_setActions(actions);
    init_setActionKeys(actionKeys);
}
function init_createDialog(){
    // Create the dialog.
    _elements.dialog = document.createElement('dialog');
    _elements.dialog.classList.add('lag-dialog');
    _elements.dialog.tabIndex = -1; // Needed so dialog is a focusable element and therefore keydown works, while -1 makes it so user CANT tab focus it.

    // Add keyboard handler or optional action keys.
    _elements.dialog.addEventListener('keydown', handleKeydown);

    // Create and append child elements...

    _elements.header = document.createElement('header');
    _elements.header.classList.add('lag-dialog-header');
    _elements.dialog.appendChild(_elements.header);

    _elements.content = document.createElement('div');
    _elements.content.classList.add('lag-dialog-content');
    _elements.dialog.appendChild(_elements.content);

    _elements.actions = document.createElement('div');
    _elements.actions.classList.add('lag-dialog-actions');
    _elements.dialog.appendChild(_elements.actions);

    // Append dialog to body (first position).
    document.body.insertBefore(_elements.dialog, document.body.firstChild);
    // Polyfill the dialog, <dialog> is not supported everywhere (as of this writing).
    dialogPolyfill.registerDialog(_elements.dialog);
}
function init_setHeader(header) {
    _elements.header.innerText = header;
    _elements.header.hidden = !header;
}
function init_setContent(content) {
    _elements.content.innerHTML = ''; // Clear content.
    _elements.content.hidden = !content;
    if (!content) return;
    if (content.root && content.root instanceof DocumentFragment) content = content.root; // When content is a TemplateInstance produced by templatizer.

    if (typeof content === 'string') {
        _elements.content.innerText = content; // innerText will turn \n chars into <br> (unlike textContent)
    }
    else if (content instanceof Element || content instanceof DocumentFragment) {
        _elements.content.appendChild(content);
    }
    else { // Invalid type for content.
        _elements.content.hidden = true;
    }
}
function init_setActions(actions) {
    _elements.actions.innerHTML = '';
    _elements.actions.hidden = !actions;

    if (typeof actions === 'string') {
        init_addAction(0, actions);
    }
    else if (actions instanceof Array) {
        // For each action we want to add a button.
        for (let i = 0; i < actions.length; ++i) {
            init_addAction(i, actions[i]);
        }
    }
    else { // Invalid type for actions.
        _elements.actions.hidden = true;
    }

    const primaryButton = _elements.actions.lastElementChild;
    if (primaryButton) primaryButton.classList.add('lag-dialog-actions-primary');
}
function init_addAction(index, text) {
    // If no text then don't add a button, but don't error either as we want to allow
    // gaps in the indexes if desired, this allows buttons to be conditionally omitted without changing the indexes.
    if (!text) return;

    const button = document.createElement('button');
    button.innerText = text;

    button.addEventListener('click', _ev => context.close(index));

    _elements.actions.appendChild(button);
}
function init_setActionKeys(actionKeys) {
    if (!actionKeys) { return; } // No actionKeys, abort.

    if (typeof actionKeys === 'string') {
        actionKeys = [actionKeys]; // Just a single value, normalize to array.
    }

    if (actionKeys instanceof Array) {
        _currentActionKeys.splice(0, _currentActionKeys.length, ...actionKeys); // Replace with new actionKeys.
    }
    else { // Invalid type for actionKeys.
        _currentActionKeys.splice(0, _currentActionKeys.length); // Clear.
    }
}

function openDialog() {
    // If already open then don't do it again (errors in chrome if you do).
    if (!_elements.dialog.open) {
        _elements.dialog.showModal();
        _elements.actions.querySelectorAll('button').forEach(ele => ele.blur());
    }
}

function handleKeydown(ev) {
    if (ev && ev.key && _currentActionKeys.includes(ev.key)) {
        ev.preventDefault(); // The native/polyfilled dialog will handle some events (eg Escape), so prevent that.

        context.close(ev.key);
    }
}

const context = {
    _callback: null,

    then(callback) {
        if (!callback || typeof callback !== 'function') {
            this._callback = null;
            return;
        }

        return new Promise(res => {
            this._callback = (choice) => {
                const val = callback ? callback(choice, _elements.content, this) : choice;

                // If val is defined but NOT a promise but DOES have a 'then' method,
                // then resolve with that then call's result rather than the val (thenable object) itself.
                // This allows 'thenable' objects to behave like a promise.
                if (val && !(val instanceof Promise) && val.then instanceof Function)
                    val.then(v => res(v));
                else
                    res(val);
            };
        });
    },

    /**
     * Manually close the dialog. Normally the user would click one of the action buttons which will close the dialog,
     * however you can forceably close the dialog via this close method.
     */
    close(choice) {
        _elements.dialog.close();

        if (this._callback) {
            return this._callback(choice);
        }
        else return choice;
    },

    /**
    * Manually re-open the dialog. This is generally not used since calling the dialog(...) function will automatically
    * open the dialog anyways, but this method is useful to re-open a dialog without having to re-initialize it from scratch.
    *
    * IMPORTANT: The 'then' method of dialog takes a callback, this callback WILL be called each time the dialog is closed,
    * even if you use this reopen call. HOWEVER, the promise returned by the 'then' method will ONLY RESOLVE ONCE, as promises
    * cannot resolve more than one time. So if you're relying on the promise's resolution it will only occur the first time the dialog closes.
    * Eg: dialog(...).then(dialogCallback).then(promiseCallback); // The dialogCallback will execute every close, but the promiseCallback will only execute on the first time.
    */
    reopen() {
        openDialog();
    }
}

/**
 * Shows a dialog according to provided arguments. You may omit content argument if desired, eg:
 *      dialog('Hello', 'Close')
 *      dialog('Hello', ['No', 'Yes'])
 *      dialog('Hello', 'Hope you are well', 'Close')
 *      dialog('Hello', 'Hope you are well', ['No', 'Yes'])
 *      dialog('Hello', 'Hope you are well', ['No', 'Yes'], ['Enter', 'Escape'])
 *
 * @param {String} header - Mandatory header text.
 * @param {String} [content] - Optional content displayed below the header. Usually a string but an Element or DocumentFragment can also be provided.
 * @param {Object} actions - Actions string/array/null. Buttons will be created with the specified text(s).
 *      All buttons will close the dialog automatically and trigger the dialog's then method (if provided).
 * @param {Object} actionKeys - Key events to handle where the values provided match event.key, eg event.key === 'Enter'.
 *
 *      Note that the dialog call doesnt actually return a Promise object, the then method is a custom implementation which itself returns a Promise.
 *      That Promise will return your callback's return value (which was passed into the then method), or if no callback then it will return the choice value.
 *      The callback specified to the then method takes one parameter receiving the resulting button choice (ie button's index as per the actions array)
 *      or the event.key value on key event if the key value was specified in actionKeys. Eg:
 *
 *          dialog('Hello', 'Hope you are well', ['Cancel', 'Panic', 'Confirm'], ['Enter']).then(choice => {
 *              switch(choice) { case 0: cancel(); break; case 1: panic(); break; case 2: confirm(); break; case 'Enter': enterTheDragon(); break; default: break; }
 *          });
 */
export function dialog(header, content, actions, actionKeys) {
    // Overload logic - if dialog(header, actions) then need to adjust argument values accordingly.
    if (actions === undefined && content && (typeof content === 'string' || content instanceof Array)) {
        actions = content;
        content = null;
    }

    context._callback = null; // Clear any previous callback.

    init(header, content, actions, actionKeys);

    openDialog();

    return context;
}